import {SpriteGraphics} from '@fantasmagames/core/src/display/SpriteGraphics';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {AnimationNames} from '@fantasmagames/slot/src/animations/AnimationNames';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {SettingsViewEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsViewEvents';
import {ISettings} from '@fantasmagames/slot/src/common/settings/_interfaces/ISettings';

export interface ISettingsData {
	musicActive: boolean;
	soundActive: boolean;
	spacebarActive: boolean;
	skipIntroActive: boolean;
	dragReelToSpinActive: boolean;
	fastPlayActive: boolean;
	quickStopActive: boolean;
}

export class SettingsView extends View {
	protected _spineLayout: Spine;
	protected backgroundColor: number = 0xd544fa;

	protected _musicActive: boolean = true;
	protected _soundActive: boolean = true;
	protected _spacebarActive: boolean = true;
	protected _skipIntroActive: boolean = true;
	protected _dragReelToSpinActive: boolean = true;

	protected _musicToggle: Spine;
	protected _soundToggle: Spine;
	protected _spacebarSpinToggle: Spine;
	protected _skipIntroToggle: Spine;

	protected _musicText: PIXI.Text;
	protected _soundText: PIXI.Text;
	protected _spacebarSpinText: PIXI.Text;
	protected _skipIntroText: PIXI.Text;

	protected _descriptionStyle: PIXI.TextStyle;
	protected _specialGermanDesktopsStyle: PIXI.TextStyle;
	protected _specialDescriptionStyle: PIXI.TextStyle;

	protected _visible: boolean;

	public init(): void {
		super.init();

		this.hideSettings();
	}

	public hideDisabledSettings(settings: ISettings): void {

	}

	public setupSettingsViewComponents(previousSettings: ISettingsData): void {
		this.soundActive = previousSettings.soundActive;
		this.musicActive = previousSettings.musicActive;
		this.skipIntroActive = previousSettings.skipIntroActive;
		this.spacebarActive = previousSettings.spacebarActive;

		this.setupMainLayout();
		this.setupBackgroundBars();
		this.setupTextStyles();
		this.setupCheckboxes();
		this.setupText();
		// this.setupSubtitlesTexts();
		this.setupCheckboxesEvents();

		this.translateCustom();

		this.musicActive ? this.emitMusicToggleOn() : this.emitMusicToggleOff();
		this.soundActive ? this.emitSoundToggleOn() : this.emitSoundToggleOff();
		this.skipIntroActive ? this.emitSkipIntroToggleOn() : this.emitSkipIntroToggleOff();
		this.spacebarActive ? this.emitSpacebarToggleOn() : this.emitSpacebarToggleOff();
	}

	public showSettings(): void {
		this.container.visible = true;
	}

	public hideSettings(): void {
		this.container.visible = false;
	}

	public allSoundsOff(): void {
		this.onMusicToggle(null, true);
		this.onSoundToggle(null, true);
	}

	public allSoundsOn(): void {
		this.onMusicToggle(null, false);
		this.onSoundToggle(null, false);
	}

	protected translateCustom(): void {
		const musicText: string = this.translator.getObject('BaseStrings', 'generic', 'g_music') || 'MUSIC';
		this._musicText.text = musicText.toUpperCase();

		const soundText: string = this.translator.getObject('BaseStrings', 'generic', 'g_soundEffects') || 'SOUND EFFECTS';
		this._soundText.text = soundText.toUpperCase();

		if (this._spacebarSpinText) {
			const spacebarSpinText: string = this.translator.getObject('BaseStrings', 'generic', 'g_spacebartospin') || 'SPACEBAR TO SPIN';
			this._spacebarSpinText.text = spacebarSpinText.toUpperCase();
		}

		const skipIntroText: string = this.translator.getObject('BaseStrings', 'generic', 'g_skipintro') || 'SKIP INTRO';
		this._skipIntroText.text = skipIntroText.toUpperCase();

		this.setDescriptionLineBreak();
	}

	protected setDescriptionLineBreak(): void {
		const leftX = this._musicText.getGlobalPosition().x;
		const rightX = this._musicToggle.getGlobalPosition().x - this._musicToggle.width / 2; // offset the end of the line to the start of the musictogglebutton. This assumes the musicToggle has an anchor set at 0.5.
		const distanceX = Math.abs(rightX - leftX);
		this._descriptionStyle.wordWrapWidth = distanceX;
		this._specialGermanDesktopsStyle.wordWrapWidth = distanceX;
		this._specialDescriptionStyle.wordWrapWidth = distanceX;
	}

	protected setupResponsibleGaming(): void {}

	protected setupTextStyles(): void {
		this._descriptionStyle = this.textStyles.settingsDescription;
		this._specialGermanDesktopsStyle = new PIXI.TextStyle({
			fontFamily: this.textStyles.settingsDescription.fontFamily,
			align: 'left',
			fontSize: 30,
			fill: '#ffe846',
			leading: 7,
			lineHeight: 0.95 * 30,
			letterSpacing: 0,
			wordWrap: true,
		});

		if (this.device.desktop && this.resources.getLocale() === 'de') {
			this._specialDescriptionStyle = new PIXI.TextStyle(this._specialGermanDesktopsStyle);
		} else {
			this._specialDescriptionStyle = new PIXI.TextStyle(this._descriptionStyle);
		}
	}

	protected setupMainLayout(): void {
		this._spineLayout = new Spine(this.resources.getSpine('SETTINGS_MENU.json'));
	}

	protected setupCheckboxes(): void {
		const yOffset = this._spineLayout.skeleton.findBone('SETTINGS_TITLE_TEXTS').data.y;
		const xOffset = 0;

		const spineName = 'SETTINGS_CONFIG_BUTTON.json';

		this._musicToggle = this.setupSettingCheckbox(spineName, 'SETTINGS_CONFIG_MUSIC', this.musicActive, xOffset, yOffset);
		this._soundToggle = this.setupSettingCheckbox(spineName, 'SETTINGS_CONFIG_SOUNDEFFECTS', this.soundActive, xOffset, yOffset);
		if (!this.device.isMobile) {
			this._spacebarSpinToggle = this.setupSettingCheckbox(spineName, 'SETTINGS_CONFIG_SPACEBAR', this.spacebarActive, xOffset, yOffset);
		}
		this._skipIntroToggle = this.setupSettingCheckbox(spineName, 'SETTINGS_CONFIG_SKIPINTRO', this.skipIntroActive, xOffset, yOffset);
	}

	protected setupSettingCheckbox(
		spineAnimation: string,
		referenceBoneName: string,
		isActive: boolean,
		xOffset: number,
		yOffset: number,
	): Spine {
		const itemReference = new Spine(this.resources.getSpine(spineAnimation));
		const itemReferenceBone = this._spineLayout.skeleton.findBone(referenceBoneName).data;
		this.container.addChild(itemReference);
		this.setTransform(itemReference, itemReferenceBone, xOffset, yOffset);
		itemReference.state.setAnimation(0, this.getToggleAnimation(isActive), false);
		itemReference.update(0);

		return itemReference;
	}

	protected setupText(): void {
		const yOffset = this._spineLayout.skeleton.findBone('SETTINGS_TITLE_TEXTS').data.y;

		const musicTextBone = this._spineLayout.skeleton.findSlot('SETTINGS_TEXT_MUSIC').bone.data;
		musicTextBone.x -= 200;

		this._musicText = this.setupSingleText(
			'MUSIC',
			{
				name: 'SETTINGS_TEXT_MUSIC',
				bone: true,
			},
			0,
			yOffset,
			musicTextBone.x,
			null,
		);
		this._soundText = this.setupSingleText(
			'SOUND',
			{name: 'SETTINGS_TEXT_SOUNDEFFECTS', bone: true},
			0,
			yOffset,
			musicTextBone.x,
			null,
		);

		if (this.device.desktop) {
			this._spacebarSpinText = this.setupSingleText(
				'SPACEBAR TO SPIN',
				{name: 'SETTINGS_TEXT_SPACEBAR', bone: true},
				0,
				yOffset,
				musicTextBone.x,
				null,
			);
		}

		this._skipIntroText = this.setupSingleText(
			'SKIP INTRO',
			{name: 'SETTINGS_TEXT_SKIPINTRO', bone: true},
			0,
			yOffset,
			musicTextBone.x,
			null,
		);

		if (this.device.isMobile && this._spacebarSpinText) {
			this._spacebarSpinText.visible = false;
		}
	}

	/*
	protected setupSubtitlesTexts(): void {
		const yOffset = this._spineLayout.skeleton.findBone('SETTINGS_TITLE_TEXTS').data.y;

		this._subtitleSoundText = new PIXI.Text('SOUND', this._subtitleStyle);
		this._subtitleSpinOptionsText = new PIXI.Text('OPTIONS', this._subtitleStyle);
		// Adds both the attachment (Slot) and bone position together.
		const subtitleSoundTextBone = this._spineLayout.skeleton.findSlot('BACKGROUND_SOLID_SOUND').bone.data;
		const subtitleSpinOptsTextBone = this._spineLayout.skeleton.findSlot('BACKGROUND_SOLID_SPINOPTIONS').bone.data;
		subtitleSoundTextBone.y += (this._spineLayout.skeleton.findSlot('BACKGROUND_SOLID_SOUND')
			.attachment as PIXI.spine.core.RegionAttachment).y;
		subtitleSpinOptsTextBone.y += (this._spineLayout.skeleton.findSlot('BACKGROUND_SOLID_SPINOPTIONS')
			.attachment as PIXI.spine.core.RegionAttachment).y;

		this.setTransform(this._subtitleSoundText, subtitleSoundTextBone, 0, yOffset);
		this.setTransform(this._subtitleSpinOptionsText, subtitleSpinOptsTextBone, 0, yOffset);

		this._subtitleSoundText.anchor.set(0.5, 0.5);
		this._subtitleSpinOptionsText.anchor.set(0.5, 0.5);

		this.container.addChild(this._subtitleSoundText);
		this.container.addChild(this._subtitleSpinOptionsText);
	}
*/
	protected setupSingleText(
		text: string,
		referenceSlot: {name: string; bone: boolean},
		xOffset: number,
		yOffset: number,
		boneX: number = null,
		boneY: number = null,
		style?: PIXI.TextStyle,
	): PIXI.Text {
		const currentStyle: PIXI.TextStyle = style ? style : this._descriptionStyle;
		const textItem = new PIXI.Text(text, currentStyle);
		let textBone = null;

		// mixing bones and slots because the Spine file has inconsistencies in naming.
		if (referenceSlot.bone) {
			textBone = this._spineLayout.skeleton.findBone(referenceSlot.name).data;
		} else {
			textBone = this._spineLayout.skeleton.findSlot(referenceSlot.name).bone.data;
		}

		if (boneX) {
			textBone.x = boneX;
		}

		if (boneY) {
			textBone.y = boneY;
		}

		this.setTransform(textItem, textBone, xOffset, yOffset);
		textItem.anchor.set(0, 0.5);
		this.container.addChild(textItem);

		return textItem;
	}

	protected setupBackgroundBars(): void {}

	protected createBackgroundBar(spineLayout: Spine, spineSlot: string, oldSlot: string): void {
		const bb1Data = spineLayout.skeleton.findSlot(spineSlot).attachment as PIXI.spine.core.RegionAttachment;

		const y1Offset = this.spineUtils.getBoneAbsolutePosition(this._spineLayout.skeleton.data, oldSlot).y;

		const bg1 = SpriteGraphics.makeRectangle(bb1Data.width, bb1Data.height, this.backgroundColor, 1);
		this.setTransform(bg1, bb1Data, 0, y1Offset);
		bg1.anchor.set(0.5, 0.5);
		this.container.addChild(bg1);
	}

	protected setTransform(
		baseobject: PIXI.DisplayObject,
		newTransformdata: PIXI.spine.core.BoneData | PIXI.spine.core.RegionAttachment,
		extraOffsetX: number = 0,
		extraOffsetY: number = 0,
	): void {
		const resolutionHeight = this.resolutionManager.resolution.height;
		const centerScreenX = this.resolutionManager.halfResolution.width;
		const centerScreenY = this.resolutionManager.halfResolution.height;
		const xPosition = centerScreenX + newTransformdata.x + extraOffsetX;
		const yPosition = resolutionHeight - (centerScreenY + newTransformdata.y + extraOffsetY);

		baseobject.setTransform(xPosition, yPosition, newTransformdata.scaleX, newTransformdata.scaleY);
	}

	protected setupCheckboxesEvents(): void {
		this._musicToggle.interactive = this._musicToggle.buttonMode = true;
		this._soundToggle.interactive = this._soundToggle.buttonMode = true;
		this._skipIntroToggle.interactive = this._skipIntroToggle.buttonMode = true;

		if (this.device.isMobile) {
			this._musicToggle.on('touchstart', this.onMusicToggle.bind(this));
			this._soundToggle.on('touchstart', this.onSoundToggle.bind(this));
			this._skipIntroToggle.on('touchstart', this.onSkipIntroToggle.bind(this));
		} else {
			this._spacebarSpinToggle.interactive = this._spacebarSpinToggle.buttonMode = true;
			this._musicToggle.on('mousedown', this.onMusicToggle.bind(this));
			this._soundToggle.on('mousedown', this.onSoundToggle.bind(this));
			this._spacebarSpinToggle.on('mousedown', this.onSpacebarToggle.bind(this));
			this._skipIntroToggle.on('mousedown', this.onSkipIntroToggle.bind(this));
		}
	}

	protected toggleItem(bool: boolean, spineToggle: Spine, emitOn: Function, emitOff: Function): boolean {
		if (bool) {
			emitOff();
		} else {
			emitOn();
		}
		spineToggle.state.setAnimation(0, this.getToggleAnimation(!bool), true);
		spineToggle.update(0);

		return !bool;
	}

	protected onSpacebarToggle(): void {
		this._spacebarActive = this.toggleItem(
			this._spacebarActive,
			this._spacebarSpinToggle,
			this.emitSpacebarToggleOn.bind(this),
			this.emitSpacebarToggleOff.bind(this),
		);
	}

	protected onSkipIntroToggle(): void {
		this._skipIntroActive = this.toggleItem(
			this._skipIntroActive,
			this._skipIntroToggle,
			this.emitSkipIntroToggleOn.bind(this),
			this.emitSkipIntroToggleOff.bind(this),
		);
	}

	protected onSoundToggle(event: PIXI.interaction.InteractionEvent, active: boolean = this._soundActive): void {
		if (this._soundToggle) {
			this._soundActive = this.toggleItem(
				active,
				this._soundToggle,
				this.emitSoundToggleOn.bind(this),
				this.emitSoundToggleOff.bind(this),
			);
		}
	}

	protected onMusicToggle(event: PIXI.interaction.InteractionEvent, active: boolean = this._musicActive): void {
		if (this._musicToggle) {
			this._musicActive = this.toggleItem(
				active,
				this._musicToggle,
				this.emitMusicToggleOn.bind(this),
				this.emitMusicToggleOff.bind(this),
			);
		}
	}

	protected emitMusicToggleOff(): void {
		this.emit(SettingsViewEvents.TOGGLE_MUSIC_OFF);
	}

	protected emitSoundToggleOff(): void {
		this.emit(SettingsViewEvents.TOGGLE_SOUND_OFF);
	}

	protected emitQuickspinToggleOff(): void {
		this.emit(SettingsViewEvents.TOGGLE_QUICKSPIN_OFF);
	}

	protected emitSkipIntroToggleOff(): void {
		this.emit(SettingsViewEvents.TOGGLE_SKIP_INTRO_OFF);
	}

	protected emitSkipIntroToggleOn(): void {
		this.emit(SettingsViewEvents.TOGGLE_SKIP_INTRO_ON);
	}

	protected emitSpacebarToggleOff(): void {
		this.emit(SettingsViewEvents.TOGGLE_SPACEBAR_OFF);
	}

	protected emitFastPlayToggleOff(): void {
		this.emit(SettingsViewEvents.TOGGLE_FAST_PLAY_OFF);
	}

	protected emitMusicToggleOn(): void {
		this.emit(SettingsViewEvents.TOGGLE_MUSIC_ON);
	}

	protected emitSoundToggleOn(): void {
		this.emit(SettingsViewEvents.TOGGLE_SOUND_ON);
	}

	protected emitQuickspinToggleOn(): void {
		this.emit(SettingsViewEvents.TOGGLE_QUICKSPIN_ON);
	}

	protected emitSpacebarToggleOn(): void {
		this.emit(SettingsViewEvents.TOGGLE_SPACEBAR_ON);
	}

	protected emitFastPlayToggleOn(): void {
		this.emit(SettingsViewEvents.TOGGLE_FAST_PLAY_ON);
	}

	protected getToggleAnimation(isActive: boolean): string {
		return isActive ? AnimationNames.SETTINGS_CONFIG_TOGGLE.Name.ACTIVE : AnimationNames.SETTINGS_CONFIG_TOGGLE.Name.INACTIVE;
	}

	public set musicActive(value: boolean) {
		this._musicActive = value;
	}

	public get musicActive(): boolean {
		return this._musicActive;
	}

	public set soundActive(value: boolean) {
		this._soundActive = value;
	}

	public get soundActive(): boolean {
		return this._soundActive;
	}

	public set spacebarActive(value: boolean) {
		this._spacebarActive = value;
	}

	public get spacebarActive(): boolean {
		return this._spacebarActive;
	}

	public set skipIntroActive(value: boolean) {
		this._skipIntroActive = value;
	}

	public get skipIntroActive(): boolean {
		return this._skipIntroActive;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsView.ts